import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
} from '@fluentui/react-components';
import { createElement } from 'react';
import { useSignalR, resolveRuntimeHubUrl } from './useSignalR';
import { useAuth } from '@app/providers/AuthProvider';

// Matches backend NotificationMessage (Blazor: NotificationMessage.cs)
interface NotificationMessage {
  type: string;
  url: string;
  title?: string;
  message?: string;
  data: Record<string, string>;
  level: number; // 0=Error, 1=Warning, 2=Info, 3=Success
  showNotification: boolean;
  showToast: boolean;
}

// Map notification type to human-readable message (matching Blazor localization)
function formatNotificationMessage(notification: NotificationMessage): string {
  let message = notification.message || notification.type;
  // Replace {key} placeholders with data values (same as Blazor CreateNotificationMessage)
  if (notification.data) {
    for (const [key, value] of Object.entries(notification.data)) {
      message = message.replace(`{${key}}`, value);
    }
  }
  return message;
}

function getToastIntent(level: number): 'error' | 'warning' | 'info' | 'success' {
  switch (level) {
    case 0: return 'error';
    case 1: return 'warning';
    case 3: return 'success';
    default: return 'info';
  }
}

// Browser Notifications API (mirrors Blazor's wwwroot/js/notifications.js)
function isBrowserNotificationSupported(): boolean {
  return 'Notification' in window;
}

function showBrowserNotification(title: string, body: string, clickUrl?: string) {
  if (!isBrowserNotificationSupported()) return;
  if (Notification.permission !== 'granted') return;

  const n = new Notification(title, {
    body,
    icon: '/icon-512.png',
  });

  if (clickUrl) {
    n.onclick = () => {
      window.focus();
      window.location.href = clickUrl;
      n.close();
    };
  }
}

/**
 * Connects to the notifications SignalR hub and shows toasts for incoming notifications.
 * Mirrors Blazor's MainLayout.razor.cs ConnectMessageHub() behavior.
 */
export function useNotificationHub() {
  const { user } = useAuth();
  // Use SSO ID (Keycloak sub claim) for hub registration — matches Blazor's GetSsoId()
  const ssoId = user?.userId;
  const queryClient = useQueryClient();
  const { dispatchToast } = useToastController();
  const dispatchToastRef = useRef(dispatchToast);
  dispatchToastRef.current = dispatchToast;

  const hubUrl = resolveRuntimeHubUrl(window.__ENV__?.SIGNALR_HUB_URL)
    || import.meta.env.VITE_SIGNALR_HUB_URL
    || '/bff/hubs/messages';

  const { invoke, on, isConnected } = useSignalR({
    hubUrl,
    onConnected: () => {
      console.log('[NotificationHub] Connected to notification hub');
    },
    onDisconnected: (err) => {
      if (err) console.warn('[NotificationHub] Disconnected:', err);
    },
  });

  // Register SSO ID with hub when connected (same as Blazor's RegisterUserId with GetSsoId())
  useEffect(() => {
    if (!isConnected || !ssoId) return;

    invoke('RegisterUserId', ssoId).catch((err) =>
      console.error('[NotificationHub] Failed to register user:', err)
    );
  }, [isConnected, ssoId, invoke]);

  // Listen for NotifyUser messages (same as Blazor's _hub.On<NotificationMessage>("NotifyUser", ...))
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = on('NotifyUser', (raw: unknown) => {
      const message = raw as NotificationMessage;
      console.log('[NotificationHub] Received notification:', message);

      // Refresh notification list and header badge count
      // Query key pattern: ['/api/v1/notifications/{userId}']
      queryClient.invalidateQueries({ predicate: (query) =>
        typeof query.queryKey[0] === 'string' && query.queryKey[0].includes('/api/v1/notifications/')
      });

      // Show toast (matching Blazor's ShowToast behavior)
      if (message.showToast) {
        const intent = getToastIntent(message.level);
        const body = formatNotificationMessage(message);

        dispatchToastRef.current(
          createElement(Toast, null,
            createElement(ToastTitle, null, message.title || message.type),
            createElement(ToastBody, null, body)
          ),
          { intent, timeout: 5000 }
        );
      }

      // Show browser desktop notification (matching Blazor's ShowNotification behavior)
      if (message.showNotification) {
        const body = formatNotificationMessage(message);
        showBrowserNotification(
          message.title || message.type,
          body,
          message.url || undefined
        );
      }
    });

    return unsubscribe;
  }, [isConnected, on, queryClient]);

  return { isConnected };
}
